/**
 * OrderAccess — the capability projection: what an email link may see
 * without a session and without a function. Possession of the path IS the
 * authorization.
 *
 * Path: `workspaces/{orgId}/orderAccess/{token}` — token = CSPRNG; `get`
 * allowed, `list` denied. NO client write path — only the server-side
 * fulfillment fan-out writes it: fulfillOrder create-guards the doc itself,
 * fills `tickets[]`, and seeds every `artifacts` slot to its opening state —
 * `pending` for a task this deployment will perform, `never` for one it
 * will not. Each delivery-task processor then moves its own slot on to
 * `stored` (or, for the Google Wallet slot, `ready` — the passes exist in
 * Google, and mintWalletSaveLinks.action.ts signs a save link on demand
 * rather than one being stored here) once that artifact actually exists.
 *
 * Carries a fulfillment-time `event`/`venue` snapshot (below) so the door
 * material — name, poster, schedule, venue, entrance notes — renders
 * identically whether this doc is read cold (a fulfillment-email link, no
 * session, no other document reachable from the token alone) or in-session
 * (a client-minted projection of the same shape, mirrored from the buyer's
 * own live checkout/event state). The snapshot is resolved once, from the
 * same catalog read `fulfillOrder`'s PDF/.ics/wallet rendering already
 * performs — never a live join the client itself would have to make, which
 * would either strand the cold reader (Event/Location need `eventId`, not
 * derivable from the token alone) or let a post-purchase venue edit drift
 * the in-app ticket out of sync with the PDF the same order already mailed.
 *
 * Deliberately excludes `buyerEmail`: this is a bare-capability token —
 * anyone holding the link (forwarded, screenshotted, synced browser
 * history) can open it with no further check, so THIS DOCUMENT carries no
 * PII of its own. Do not add an email/name field here; the confirmation
 * copy is written to never need one. One exception rides the same token
 * outside this schema: `files/{orderAccessToken}/**` (storage.rules) is
 * also the Storage path prefix for the buyer's own required § 14 receipt,
 * which does carry the buyer's name and, above the § 33 UStDV ceiling,
 * their address — reachable by the same link, by design, since the receipt
 * is the buyer's own document.
 */
import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import { z } from "zod";
import { EventSchema } from "./event.entity.ts";
import { LocationSchema } from "./location.entity.ts";
import { OrderStatusSchema } from "./order.entity.ts";
import {
	SecretToken,
	TicketSchema,
	TicketStatusSchema,
} from "./ticket.entity.ts";
import { TicketTemplateVariantSchema } from "./ticket-template-variant.const.ts";
import type { Actor } from "./workspace.entity.ts";

// One artifact slot's whole lifecycle, closed over three states rather than
// a tag-plus-optional-path bag: `never` is a stored sentinel (a missing key
// would fail `OrderAccessSchema.safeParse` for the whole document the moment
// Firestore returns null for it — see orderAccessToken's own describe on
// order.entity.ts). This schema covers only Storage-backed artifacts —
// Apple's `.pkpass` included, a stored object — so it carries no
// provider-hosted variant; the Google Wallet slot's own lifecycle
// (WalletGoogleSlotSchema below) is never a Storage path at all.
export const ArtifactSlotSchema = z.discriminatedUnion("state", [
	z.object({ state: z.literal("never") }), // not for this order, ever — renders no chip
	z.object({ state: z.literal("pending") }), // expected, not yet rendered
	z.object({
		state: z.literal("stored"),
		path: z
			.string()
			.describe(
				"the Storage object path under files/{orderAccessToken}/ this artifact resolves to",
			),
	}),
]);
export type ArtifactSlot = z.infer<typeof ArtifactSlotSchema>;

// The Google Wallet slot's own lifecycle — distinct from ArtifactSlotSchema
// because its third state carries no payload at all: `ready` records only
// that the passes exist in Google, never a URL. A save link is minted on
// demand by mintWalletSaveLinks.action.ts when the buyer's page renders it,
// not stored here — the service account's signing key is documented valid
// "at least 12 hours" with no published schedule for when it stops
// resolving, so a link persisted at fulfillment and clicked days later bets
// on that key; a link signed while the buyer is looking at the page cannot
// lose that bet.
export const WalletGoogleSlotSchema = z.discriminatedUnion("state", [
	z.object({ state: z.literal("never") }),
	z.object({ state: z.literal("pending") }),
	z.object({ state: z.literal("ready") }),
]);
export type WalletGoogleSlot = z.infer<typeof WalletGoogleSlotSchema>;

// The issued ticket files — one Storage path per ticket, index-aligned to
// tickets[]. Distinct from ArtifactSlotSchema because one order's slot
// carries every ticket's own file rather than a single path — an issued
// ticket file is always a Storage object, never provider-hosted.
export const TicketFilesSlotSchema = z.discriminatedUnion("state", [
	z.object({ state: z.literal("never") }),
	z.object({ state: z.literal("pending") }),
	z.object({
		state: z.literal("stored"),
		paths: z
			.array(z.string())
			.describe(
				"paths[i] is tickets[i]'s file — written once, in the finalize txn, aligned to the frozen tickets[] order",
			),
	}),
]);
export type TicketFilesSlot = z.infer<typeof TicketFilesSlotSchema>;

// The Apple Wallet slot's own lifecycle — one signed .pkpass per ticket plus
// the one `.pkpasses` bundle that carries all of them. `paths[]`/`passes[]`
// keep the per-ticket shape TicketFilesSlotSchema carries for the same
// reason: a single ticket's pass is addressable on its own, and the
// individual files are what an email attaches and a non-Safari browser
// downloads. The `stored` member addresses those two ways: `paths[]`,
// positional and index-aligned to tickets[] — required, since it is the
// shape every already-stored doc was written with, and `walletApple` sits
// inside a whole-document parse (order-access.api.ts) that a missing
// required field fails outright; and `passes[]`, one {ticketId,path} entry
// per ticket, so a consumer resolves a specific ticket's pass by identity
// rather than trusting the array to stay in the order it was written.
// `passes` is nullish, not required, because a doc stored before this field
// existed never wrote it — `paths[]` alone still resolves those.
// `bundlePath` is nullish for that same reason, and it is what gives the
// order ONE wallet control the way the Google save link already has one.
// Why an order's passes were not packaged into one `.pkpasses` bundle —
// the order-access view renders this where the one-tap "add all to Wallet"
// control would otherwise sit, so the buyer reads a reason rather than a
// missing button. Discriminated on `reason`: a second cause carries its own
// evidence instead of a shared bag of optionals.
// Fields justified:
//   passCount — how many passes the order actually carries, the number the
//     rendered sentence names
//   ceiling   — the limit in force when the slot was written; the view lives
//     in another workspace and would otherwise carry its own copy of a number
//     only the signer knows
export const WalletAppleBundleAbsenceSchema = z.discriminatedUnion("reason", [
	z.object({
		reason: z.literal("aboveApplePassCeiling"),
		passCount: z.number().int().positive(),
		ceiling: z.number().int().positive(),
	}),
]);
export type WalletAppleBundleAbsence = z.infer<
	typeof WalletAppleBundleAbsenceSchema
>;

export const WalletAppleSlotSchema = z.discriminatedUnion("state", [
	z.object({ state: z.literal("never") }),
	z.object({ state: z.literal("pending") }),
	z.object({
		state: z.literal("stored"),
		paths: z.array(z.string()).min(1),
		passes: z
			.array(
				z.object({
					ticketId: z.string().min(1),
					path: z.string().min(1),
				}),
			)
			.nullish(),
		bundlePath: z
			.string()
			.min(1)
			.nullish()
			.describe(
				"the Storage object path of the order's `.pkpasses` bundle — one ZIP of every pass in `paths[]`, which iOS 15+ adds to Wallet in full from a single link; absent on a doc stored before the bundle existed, where the per-ticket paths are the only route",
			),
		bundleAbsence: WalletAppleBundleAbsenceSchema.nullish().describe(
			"why this order carries no `.pkpasses` bundle — absent wherever `bundlePath` resolves, and on a doc stored before the bundle was gated; where it is present the per-ticket paths are the whole route",
		),
	}),
]);
export type WalletAppleSlot = z.infer<typeof WalletAppleSlotSchema>;

// Fields justified:
//   orderStatus       — the state machine the read predicate below gates on.
//   event / venue      — the door material a buyer needs with no other
//     document reachable: the event's own name/poster/schedule plus the
//     venue resolved from the event's own locationId, at the same moment
//     fulfillOrder resolves it for the PDF/.ics/wallet artifacts. event
//     also carries templateVariant — not door material itself, but what
//     tickets.webhook.ts's own resolve loop needs to compute
//     tickets[].templateVariant, riding this same snapshot at zero extra
//     read cost rather than a second Event read.
//   tickets / artifacts — each field carries its own inline description.
// Excluded:
//   buyerEmail / buyerName — PII a bare-capability token must never carry;
//     see the file header.
//   A per-ticket-type venue override (TicketType.locationId — "the concert
//     type is in Saal 1, another in Shedhalle") — `venue` below is resolved
//     once, from the event's own locationId, and shared by every ticket in
//     the order. A genuinely mixed-venue order has no distinct-per-ticket
//     rendering path here; that is a known gap, not a silently-handled case.
export const OrderAccessSchema = z
	.object({
		orderStatus: OrderStatusSchema,
		event: EventSchema.pick({
			name: true,
			poster: true,
			logo: true,
			schedule: true,
			templateVariant: true,
		}).describe(
			"the event these tickets admit to, read once at the same moment fulfillOrder resolves the order's paid data — frozen so a later event edit never reaches an already-issued ticket",
		),
		venue: LocationSchema.pick({
			name: true,
			address: true,
			geo: true,
			url: true,
			notes: true,
			timezone: true,
		}).describe(
			"the venue resolved from the event's own locationId against the location catalog, at the same moment the PDF and .ics LOCATION field resolve it — the in-app ticket's venue material never drifts from what those stored artifacts already rendered",
		),
		tickets: z.array(
			z.object({
				ticketId: z.string(),
				typeName: z.string(),
				templateVariant: TicketTemplateVariantSchema.describe(
					"resolveTicketTemplateVariant’s output (ticket-type.entity.ts), resolved once per distinct typeId at fulfillment — never re-derived at render, so a later type/event templateVariant edit never reaches an already-issued ticket",
				),
				status: TicketStatusSchema,
				// Typed off the ticket's own field rather than re-declared, so the
				// copy and the frozen original cannot take different shapes. Nullish
				// and never defaulted, for the reason that field states: a default
				// asserts a window no operator set. Absent reaches this projection
				// two ways that read alike — a document stored before the field
				// carried no copy, and a ticket sold with no window has none to copy
				// — so a consumer grouping tickets by window treats both as the one
				// unbounded case rather than telling them apart.
				entryWindow: TicketSchema.shape.entryWindow.describe(
					"the span this ticket admits within, copied at fulfillment from the ticket’s own already-frozen window — the same fact the door decides on, never re-derived here. Absent is the unbounded case: this ticket carries no window and admits for the event’s whole span, as it was sold",
				),
				qrToken: SecretToken.describe(
					"the ticket’s own QR credential — the buyer legitimately holds their tickets, and the responsive in-app ticket renders from here",
				),
			}),
		),
		artifacts: z
			.object({
				ticketFiles: TicketFilesSlotSchema.default({ state: "never" }).describe(
					"the per-ticket issued ticket files — a doc lacking this key parses as the open `never` state a never-produced artifact carries",
				),
				ics: ArtifactSlotSchema,
				receipt: ArtifactSlotSchema,
				walletApple: WalletAppleSlotSchema, // one signed .pkpass per ticket, index-aligned to tickets[]
				walletGoogle: WalletGoogleSlotSchema,
			})
			.describe(
				"one artifact slot per delivery task this order could ever produce — every key always present and always written, each resolving `never` (not for this order, ever), `pending` (expected, not yet rendered), `stored` (a Storage path under files/{token}/), or — walletGoogle only — `ready` (the passes exist in Google; mintWalletSaveLinks.action.ts signs a save link on demand, never stored) once its own delivery task finishes",
			),
	})
	.describe(
		"what an email link may see without a session and without a function — get allowed, list denied",
	);
export type OrderAccess = z.infer<typeof OrderAccessSchema>;
// z.input, not z.infer: `ticketFiles` carries a .default(), so what a writer
// hands the schema is looser than what a parse returns.
export type OrderAccessInput = z.input<typeof OrderAccessSchema>;

// No 'list' action is declared, so a collection query falls to the
// generated default-deny tail; no write action — fulfillOrder (Admin
// SDK) is the sole, create-guarded writer.
//
// The read predicate deliberately touches `data.orderStatus` rather than
// an unconditional `and()`: an unconditionally-true condition is
// trivially list-safe, so the emitter would emit the COMBINED `allow
// read:` keyword (get *and* list — Firestore has no get-only keyword).
// Touching real doc data forces the list-safety downgrade to `allow
// get:` only. Membership over the full enum never denies a real document.
//
// The member list is spread from OrderStatusSchema.options rather than
// written out: a hand-copied list silently narrows the moment a status is
// added to the enum, and the failure mode is that buyers holding an order
// in the new status can no longer open their tickets — a denial that reads
// as correct in the emitted rules. Deriving it makes that unrepresentable.
export const OrderAccess = entity<Actor>()({
	schema: OrderAccessSchema,
	actions: ["read"] as const,
})
	.pipe(({ can }) => [
		can("read").when((data) =>
			data.orderStatus.in([...OrderStatusSchema.options]),
		),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/orderAccess/{token}",
		}),
	);
